import { format } from "date-fns";
import { useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CalendarBase } from "./CalendarBase";
import { resolveDateLocale, toIsoDayString } from "./calendar-helpers";
import { type Holiday, toHolidayMap } from "./calendar-utils";

export interface CalendarProps {
  currentDate: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  disablePrevMonth?: boolean;
  onSelectDay?: (d: Date) => void;
  isReadOnly?: boolean;
  isDaySelectable?: (day: Date) => boolean;
  getDayTitle?: (day: Date) => string | undefined;
  disabledDays?: Set<string>;
  holidays?: Holiday[];
  renderDayContent: (day: Date) => ReactNode;
  weekStartsOn?: 0 | 1;
  compact?: boolean;
}

const holidayColor = "#FFD1D2";

export function Calendar({
  currentDate,
  onPrevMonth,
  onNextMonth,
  disablePrevMonth = false,
  onSelectDay,
  isReadOnly = false,
  isDaySelectable,
  getDayTitle,
  disabledDays,
  holidays = [],
  renderDayContent,
  weekStartsOn = 1,
  compact = false,
}: CalendarProps) {
  const { i18n } = useTranslation("timeEntries");
  const dateLocale = resolveDateLocale(i18n.resolvedLanguage);
  const holidaysByDate = useMemo(
    () =>
      toHolidayMap(
        holidays.map((holiday) => ({
          ...holiday,
          date: toIsoDayString(String(holiday.date ?? "")) ?? String(holiday.date),
        })),
      ),
    [holidays],
  );

  return (
    <CalendarBase
      month={currentDate}
      onMonthChange={(next) =>
        next < currentDate ? onPrevMonth() : onNextMonth()
      }
      disablePreviousMonth={disablePrevMonth}
      weekStartsOn={weekStartsOn}
      className="border-0 p-0 shadow-none"
      isDayDisabled={(day) => {
        const key = format(day, "yyyy-MM-dd");
        const outsideMonth = format(day, "MM") !== format(currentDate, "MM");
        const selectable =
          !outsideMonth &&
          !(disabledDays?.has(key) ?? false) &&
          (isDaySelectable ? isDaySelectable(day) : true);
        return !selectable || outsideMonth;
      }}
      renderDay={(day, context) => {
        const holiday = holidaysByDate[context.dateKey];
        const partialRatio =
          typeof holiday?.partialRatio === "number" ? holiday.partialRatio : null;
        const isPartialHoliday =
          partialRatio !== null && partialRatio > 0 && partialRatio < 1;
        const partialOverlayStyle = isPartialHoliday
          ? {
              backgroundColor: holidayColor,
              maskImage: "linear-gradient(135deg, transparent 50%, black 50%)",
              WebkitMaskImage:
                "linear-gradient(135deg, transparent 50%, black 50%)",
            }
          : undefined;
        const dayNumber = format(day, "d", { locale: dateLocale });

        return (
          <button
            key={context.dateKey}
            type="button"
            onClick={() => !isReadOnly && context.isSelectable && onSelectDay?.(day)}
            disabled={context.isDisabled}
            title={getDayTitle?.(day)}
            className={[
              "group relative flex flex-col items-stretch gap-y-1 overflow-hidden rounded-xl border border-border-subtle bg-surface-primary p-2 text-left transition-colors",
              compact ? "min-h-[74px]" : "min-h-[110px]",
              holiday && !isPartialHoliday ? "bg-[var(--holiday-bg)]" : "",
              context.isOutsideMonth
                ? "pointer-events-none cursor-default opacity-40"
                : context.isDisabled
                  ? compact
                    ? "cursor-default"
                    : "cursor-default opacity-30"
                  : isReadOnly
                    ? "cursor-default"
                    : "cursor-pointer hover:bg-surface-secondary/70",
            ].join(" ")}
            style={
              holiday && !isPartialHoliday
                ? ({ ["--holiday-bg" as string]: holidayColor } as CSSProperties)
                : undefined
            }
          >
            {isPartialHoliday ? (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 transition-opacity duration-150 group-hover:opacity-0"
                style={partialOverlayStyle}
              />
            ) : null}
            <div className="flex items-center">
              {holiday ? (
                <span className="ml-1 text-sm font-semibold text-state-danger">
                  {dayNumber}
                </span>
              ) : context.isToday && !compact ? (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                  {dayNumber}
                </span>
              ) : (
                <span
                  className={`ml-1 text-sm font-semibold ${context.isOutsideMonth ? "text-content-muted/50" : "text-content-primary"}`}
                >
                  {dayNumber}
                </span>
              )}
            </div>

            {holiday?.name ? (
              <div className="mt-2 w-full">
                <div className="w-full text-[10px] font-semibold text-state-danger">
                  {holiday.name}
                </div>
              </div>
            ) : null}

            <div className="mt-1 flex-1 w-full overflow-hidden">
              {renderDayContent(day)}
            </div>
          </button>
        );
      }}
    />
  );
}

export default Calendar;
